import { ComposerTranslation } from 'vue-i18n'
import { DLVFavoriteKey } from '~/lib/models/dlv-modal'
import { FavoriteAddress } from '~/lib/services/store/auth.dto'
import { Address } from '~/lib/services/store/order/order.dto'

type ParsedRestaurantMetadata = Array<{ country: string; restaurants: { code: string }[] }>

export const addRestaurantsToFavorite = ({
  favoriteRestaurantsMetadata,
  countryCode,
  restaurantCode,
}: {
  favoriteRestaurantsMetadata?: string
  countryCode: string
  restaurantCode: string
}) => {
  if (!favoriteRestaurantsMetadata) {
    return [
      {
        country: countryCode,
        restaurants: [{ code: restaurantCode }],
      },
    ]
  }
  const parsed = JSON.parse(favoriteRestaurantsMetadata) as ParsedRestaurantMetadata
  if (!parsed.length) {
    return [
      {
        country: countryCode,
        restaurants: [{ code: restaurantCode }],
      },
    ]
  }
  return parsed.reduce((acc, countryFavs) => {
    if ({ ...countryFavs }.country === countryCode) {
      countryFavs.restaurants = [...countryFavs.restaurants, { code: restaurantCode }]
      return [...acc, countryFavs]
    } else {
      return [
        ...acc,
        countryFavs,
        { country: countryCode, restaurants: [{ code: restaurantCode }] },
      ]
    }
  }, [] as ParsedRestaurantMetadata)
}

export const removeRestaurantsFromFavorite = ({
  favoriteRestaurantsMetadata,
  countryCode,
  restaurantCode,
}: {
  favoriteRestaurantsMetadata?: string
  countryCode: string
  restaurantCode: string
}) => {
  if (!favoriteRestaurantsMetadata) {
    return []
  }
  const parsed = JSON.parse(favoriteRestaurantsMetadata) as ParsedRestaurantMetadata
  return parsed.reduce((acc, countryFavs) => {
    if ({ ...countryFavs }.country === countryCode) {
      countryFavs.restaurants = countryFavs.restaurants.filter(
        ({ code }) => code !== restaurantCode
      )
      return [...acc, countryFavs]
    } else {
      return [...acc, countryFavs]
    }
  }, [] as ParsedRestaurantMetadata)
}

export const isFavoriteRestaurant = ({
  favoriteRestaurantsMetadata,
  countryCode,
  restaurantCode,
}: {
  favoriteRestaurantsMetadata?: string
  countryCode: string
  restaurantCode?: string
}) => {
  if (!favoriteRestaurantsMetadata || !restaurantCode) {
    return false
  }
  const parsed = JSON.parse(favoriteRestaurantsMetadata) as ParsedRestaurantMetadata
  const countryFavs = parsed.find(({ country }) => country === countryCode)
  return countryFavs?.restaurants.some(favs => favs.code === restaurantCode) || false
}

export const favoriteAddressToAddress = (favoriteAddress: FavoriteAddress): Address => ({
  ...favoriteAddress,
  alias: favoriteAddress.alias,
  favoriteId: favoriteAddress._id,
})

export const addressToBodyData = (address: Address) => {
  delete address.distance
  if (!address.number) address.number = undefined
  if (!address.neighborhood) {
    delete address.neighborhood
  }
  if (!address.complement) {
    delete address.complement
  }
  if (!address.specialInstructions) {
    delete address.specialInstructions
  }
  if (!address.postalCode) {
    delete address.postalCode
  }
  if (address.favoriteId) {
    delete address.favoriteId
  }
  if ('_id' in address) {
    delete address._id
  }
  return address
}

export const aliasCodeToTranslation = ({
  alias,
  t,
}: {
  alias?: string
  t: ComposerTranslation
}) => {
  if (!alias) return ''
  switch (true) {
    case alias === DLVFavoriteKey.home:
      return t('selectRestaurant.DLV.favorite.keys.home')
    case alias === DLVFavoriteKey.work:
      return t('selectRestaurant.DLV.favorite.keys.work')
    default:
      return alias !== '-' ? alias : ''
  }
}
